/**
 * Desktop app-auth callback detection.
 *
 * The desktop app sends the browser to `/oauth/iris-studio[/:env]` (see
 * `routes/router.tsx` and `studio/electron/src/main/auth/utils.ts`). Auth0
 * mints that authorization code for the DESKTOP client and returns it to this
 * page. The page's only job is to hand the code to the desktop app over the
 * `iris://code/<code>` deep link — the browser must never try to redeem it.
 *
 * Why this predicate exists (ENG-4637 blocker): when the callback carries
 * `state`, `@auth0/auth0-react`'s `hasAuthParams()` is true and `Auth0Provider`
 * redeems the code on mount using the WEB client id. Auth0 answers 403
 * `invalid_grant`, `<Synchronizer>` throws ERR-734E during render, and the
 * `<AppAuth>` route — the thing that fires the deep link — never mounts.
 * Keeping the SDK off the callback makes the hand-off deterministic rather
 * than dependent on which side redeems the code first.
 *
 * Per rule code-comments-required (ai.knowledge/global).
 */

/** Base pathname of the desktop app-auth route (`routes/router.tsx`). */
const APP_AUTH_PATHNAME = '/oauth/iris-studio';

/** Minimal shape of `window.location` this predicate reads. */
export type AppAuthLocation = { pathname: string; search: string };

/**
 * True when the current URL is the desktop app-auth route carrying an Auth0
 * callback result (`code`, or an `error` pair). A bare visit to the route with
 * no result — the outbound leg that still has to start `/authorize` — is NOT a
 * callback, so the Auth0 SDK stays mounted for it.
 */
export const isDesktopAppAuthCallback = ({ pathname, search }: AppAuthLocation): boolean => {
  const onAppAuthRoute =
    pathname === APP_AUTH_PATHNAME || pathname.startsWith(`${APP_AUTH_PATHNAME}/`);

  if (!onAppAuthRoute) {
    return false;
  }

  const params = new URLSearchParams(search);

  return params.has('code') || params.has('error') || params.has('error_description');
};
